import { useState, useMemo, useEffect } from 'react';
import { ResourcesHeader } from './components/ResourcesHeader';
import { ResourcesContent } from './components/ResourcesContent';
import useAgenciesList from '../../hooks/useAgenciesList';
import { useFilterOptions } from '../../hooks/useFilterOptions';
import useResourcesList from '../../hooks/useResourcesList';
import { AddResourceForm } from '../../Components/ResourceForm/AddResourceForm';
import { Modal } from '../../Components/Modal/Modal';
import { AddResourceFormValues } from '../../Components/ResourceForm/types';
import { resources } from '../../api/resources';
import { useLoader } from '../../contexts/LoaderContext';
import { useAlert } from '../../contexts/AlertContext';
import { TABLE_COLUMNS } from './constants';
import { TableResource } from './types';
import noImage from '../../Assets/Images/no-img.jpg';
import { useFormatImageUrl } from '../../utils/formatImageUrl';
import { ConfirmationModal } from '../../Components/ConfirmationModal/ConfirmationModal';
import { useConfirmation } from '../../hooks/useConfirmation';
import { useStorageUrl } from '../../hooks/useStorageUrl';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../api/users';

export const Resources = () => {
  const { agenciesList } = useAgenciesList();
  const { categoryOptions } = useFilterOptions();
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();
  const formatImageUrl = useFormatImageUrl();
  const { storageUrl } = useStorageUrl();
  const { userData } = useAuth();
  const isShowAddNewResourceButton =
    userData?.role === UserRole.ADMIN || userData?.role === UserRole.DIRECTOR;

  const {
    agencyFilter,
    typeFilter,
    categoryFilter,
    currentPage,
    resourcesData,
    selectedResources,
    onAgencyFilterChange,
    onTypeFilterChange,
    onCategoryFilterChange,
    onPageChange,
    onRowSelect,
    fetchResources,
    onDeleteResource,
    onDeleteMultipleResources,
    setSelected,
    onDownloadResource,
  } = useResourcesList({ itemsPerPage: 10 });

  const {
    isOpen: isConfirmationOpen,
    config: confirmationConfig,
    handleConfirm,
    openConfirmation,
    closeConfirmation,
    handleMultipleConfirmation,
  } = useConfirmation((id) => {
    if (id) {
      onDeleteResource(id);
    } else {
      onDeleteMultipleResources(selectedResources);
      setSelected([]);
    }
  });

  const handleDeleteResource = (resourceId: number) => {
    openConfirmation(
      {
        title: 'Are you sure you want to delete this resource?',
        message:
          'This action cannot be undone. Once deleted, this resource will be permanently removed.',
        variant: 'danger',
      },
      resourceId
    );
  };

  const handleDeleteAll = () => {
    if (selectedResources.length === 0) return;

    handleMultipleConfirmation({
      title: 'Are you sure you want to delete these resources?',
      message:
        'This action cannot be undone. Once deleted, these resources will be permanently removed.',
      variant: 'danger',
    });
  };

  const selectedHasLink = resourcesData.resources.some(
    (resource) => selectedResources.includes(resource.id) && resource.type === 'link'
  );

  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImagePath, setSelectedImagePath] = useState<string | null>(null);

  // Re-format selectedImage whenever storageUrl changes
  useEffect(() => {
    if (selectedImagePath && storageUrl) {
      setSelectedImage(formatImageUrl(selectedImagePath));
    }
  }, [selectedImagePath, storageUrl, formatImageUrl]);

  const openResourceModal = () => {
    setIsResourceModalOpen(true);
  };

  const closeResourceModal = () => {
    setIsResourceModalOpen(false);
  };

  const handleResourceSubmit = async (values: AddResourceFormValues) => {
    showLoader();
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('agencyId', values.agency);
      formData.append('categoryId', values.category);

      if (values.type === 'file' && values.file) {
        formData.append('file', values.file);
      } else if (values.type === 'link' && values.link) {
        formData.append('link', values.link);
      }
      if (values.thumbnail) formData.append('thumbnail', values.thumbnail);

      await resources.create(formData);
      await fetchResources();
      closeResourceModal();
    } catch (error) {
      const message = (error as any)?.response?.data?.message || 'Failed to create resource';
      showAlert(message, 'error');
    } finally {
      hideLoader();
    }
  };

  const columns = useMemo(
    () =>
      TABLE_COLUMNS.map((col) => {
        if (col.id === 'thumbnail') {
          return {
            ...col,
            render: (row: TableResource) => (
              <div
                className="thumbnail-cell"
                onClick={(e) => {
                  const imgSrc = e.currentTarget.querySelector('img')?.src;
                  if (imgSrc && !imgSrc.includes('no-img')) {
                    setSelectedImagePath(row.thumbnail);
                    setSelectedImage(formatImageUrl(row.thumbnail));
                  }
                }}
              >
                <img
                  className="thumbnail"
                  src={formatImageUrl(row.thumbnail)}
                  alt={row.name}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = noImage;
                  }}
                />
              </div>
            ),
          };
        }
        if (col.id === 'action') {
          return {
            ...col,
            render: (
              row: TableResource,
              onDelete?: (id: number) => void,
              onDownload?: (id: number) => void
            ) => col.render?.(row, onDelete, onDownload, isShowAddNewResourceButton),
          };
        }
        return col;
      }),
    [formatImageUrl, isShowAddNewResourceButton]
  );

  return (
    <div className="resources-page">
      <ResourcesHeader
        agencyFilter={agencyFilter}
        typeFilter={typeFilter}
        categoryFilter={categoryFilter}
        onAgencyFilterChange={onAgencyFilterChange}
        onTypeFilterChange={onTypeFilterChange}
        onCategoryFilterChange={onCategoryFilterChange}
        selectedResources={selectedResources.map(String)}
        onAddResource={openResourceModal}
        agencyOptions={agenciesList.map((a) => ({ value: a.id.toString(), label: a.name }))}
        categoryOptions={categoryOptions}
        onDeleteMultipleResources={handleDeleteAll}
        setSelected={setSelected}
        hideDownloadAllButton={selectedHasLink}
      />
      <ResourcesContent
        columns={columns}
        data={resourcesData.resources}
        onRowSelect={onRowSelect}
        totalPages={resourcesData.totalPages}
        currentPage={currentPage}
        onPageChange={onPageChange}
        onDelete={handleDeleteResource}
        onDownload={onDownloadResource}
        isShowAddNewResourceButton={isShowAddNewResourceButton}
      />
      <Modal isOpen={isResourceModalOpen} onClose={closeResourceModal}>
        <AddResourceForm onSubmit={handleResourceSubmit} onCancel={closeResourceModal} />
      </Modal>
      <Modal
        className="image-modal"
        isOpen={!!selectedImage}
        onClose={() => {
          setSelectedImage(null);
          setSelectedImagePath(null);
        }}
      >
        <div className="image-modal-content">
          <img src={selectedImage || ''} alt="Fullscreen preview" />
        </div>
      </Modal>
      <Modal isOpen={isConfirmationOpen} onClose={closeConfirmation}>
        {confirmationConfig && (
          <ConfirmationModal
            {...confirmationConfig}
            onConfirm={handleConfirm}
            onCancel={closeConfirmation}
          />
        )}
      </Modal>
    </div>
  );
};
